// import UnreviewedImagesQuery from '@/graphql/image/UnreviewedImagesQuery.gql'

import apollo from '@/graphql'
import gql from 'graphql-tag'

// initial state
const state = {
  isLoadingPaginatedExtImages: false,
  extImages: [],
  paginatedExtImages: [],
  extImage: {},
  extImageAnnotations: [],
  // Extra data provided in the predict-boxes response
  extImageAnnotationMetadata: {},
  extPlacementsGuesses: [],
  extMastheads: [],
  extPockets: [],
  nextPossibleUrls: [],
  originalExtImageAnnotations: [],
  randomExtImage: null,
  rotationDegrees: undefined,
  timestamps: {},
}

// getters
const getters = {
  extImages: st => st.extImages,
  extImage: st => st.extImage,
  extImageAnnotations: st => st.extImageAnnotations,
  extPlacementsGuesses: st => st.extPlacementsGuesses,
  isLoadingPaginatedExtImages: st => st.isLoadingPaginatedExtImages,
  nextPossibleUrls: st => st.nextPossibleUrls,
  originalExtImageAnnotations: st => st.originalExtImageAnnotations,
  paginatedExtImages: st => st.paginatedExtImages,
  rotationDegrees: st => st.rotationDegrees,
}

// mutations
const mutations = {
  recordNextImageStart: (state) => {
    state.timestamps.nextImageStart = Date.now()
  },
  recordGotNextImage: (state) => {
    state.timestamps.gotNextImage = Date.now()
  },
  recordGotAnnotations: (state) => {
    state.timestamps.gotAnnotations = Date.now()
  },
  recordImageDownloaded: (state) => {
    state.timestamps.imageDownloaded = Date.now()
  },
  setExtImages: (state, extImages) => {
    state.extImages = extImages
  },
  setExtImage: (state, nextExtImage) => {
    state.extImage = nextExtImage
    /**
     * This method is not just called to load an image.
     * It's also called when updating sections.
     * Additionally, extImage has a different url every time
     * the resolver responds (thanks to the auth params in the URL).
     * We want to keep using the URL we already loaded, if applicable.
     * If we waited 3s for the image to load when we start the audit,
     * we don't want to do it a second time.
     */
    const url = state.nextPossibleUrls
      .find(u => (+u.id) === (+nextExtImage.id))
    if (url) {
      // We've already downloaded the image for this id
      state.extImage.url = url.url
    }
    // This is a temporary fix until the `getNextUrls` route is implemented.
    state.nextPossibleUrls = [{
      id: state.extImage.id,
      url: state.extImage.url,
    }]
  },
  setExtImageAnnotations: (state, extImageAnnotations) => {
    state.extImageAnnotationMetadata = {
      imgWidth: extImageAnnotations.imgWidth,
      imgHeight: extImageAnnotations.imgHeight,
      imgTs: extImageAnnotations.imgTs,
    }
    state.originalExtImageAnnotations = extImageAnnotations.boxes
    state.extImageAnnotations = extImageAnnotations.boxes.concat() // Given object is immutable
  },
  setExtMastheads: (state, extMastheads) => {
    state.extMastheads = extMastheads
  },
  setExtPlacementsGuesses: (state, extPlacementsGuesses) => {
    state.extPlacementsGuesses = extPlacementsGuesses
  },
  setExtPockets: (state, extPockets) => {
    state.extPockets = extPockets
  },
  setIsLoadingPaginatedExtImages: (state, isLoadingPaginatedExtImages) => {
    state.isLoadingPaginatedExtImages = isLoadingPaginatedExtImages
  },
  setNextPossibleUrls: (state, nextPossibleUrls) => {
    const newUrls = nextPossibleUrls
      .map((newUrl) => {
        /**
         * If we already have a url for one of these IDs,
         * use the old URL. That way, we don't request
         * the same image more than once.
         */
        const oldUrl = state.nextPossibleUrls
          .find(oUrl => (+oUrl.id) === (+newUrl.id))
        if (oldUrl) {
          return oldUrl
        }
        return newUrl
      })
    state.nextPossibleUrls = newUrls
    if (state.extImage.id) {
      state.nextPossibleUrls.push({
        id: state.extImage.id,
        url: state.extImage.url,
      })
    }
  },
  setPaginatedExtImages: (state, extImages) => {
    state.paginatedExtImages = extImages
  },
  setRandomExtImage: (state, randomExtImage) => {
    state.randomExtImage = randomExtImage
  },
  setRotationDegrees: (state, degrees) => {
    state.rotationDegrees = degrees
  },
}

// actions
const actions = {
  createMasthead(_, { name }) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation createExtMasthead($name: String!) {
          createExtMasthead(name: $name) {
            id
            name
          }
        }
      `,
      variables: { name },
    })
  },
  createPocket(_, { name }) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation createExtPocket($name: String!) {
          createExtPocket(name: $name) {
            id
            name
          }
        }
      `,
      variables: { name },
    })
  },
  extAudit(_, variables) {
    return apollo.defaultClient.mutate({
      fetchPolicy: 'no-cache',
      mutation: gql`
        mutation extAudit($json: String!) {
          extAudit(json: $json)
        }
      `,
      variables: { json: JSON.stringify(variables) },
    }).then(({ data: { extAudit } }) => JSON.parse(extAudit))
  },
  guessExtPlacements({ commit }, variables) {
    return apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: gql`
        query guessExtPlacements($imgId: Int!, $imgWidth: Int, $imgHeight: Int, $imgTs: Float, $annotations: [ExtImageAnnotationInput]!) {
          guessExtPlacements(imgId: $imgId, imgWidth: $imgWidth, imgHeight: $imgHeight, imgTs: $imgTs, annotations: $annotations) {
            signature
            clusters {
              left
              top
              right
              bottom
              hasMagazine
              masthead
              mastheadConfidence
              hasSticker
              sticker
              stickerConfidence
              suggestions {
                label
                imgId
                boxIx
                score
              }
              stickerSuggestions {
                label
                score
              }
            }
          }
        },
      `,
      variables: {
        imgId: state.extImage.id,
        ...variables,
        ...state.extImageAnnotationMetadata,
      },
    }).then(({ data: { guessExtPlacements } }) => {
      commit('setExtPlacementsGuesses', guessExtPlacements)
    })
  },
  loadPaginatedExtImages({ commit }, { variables }) {
    commit('setIsLoadingPaginatedExtImages', true)
    return apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: gql`
        query PaginatedExtImages(
          $pageSize: Int!
          $page: Int!
          $search: String
          $filters: [String]
          $sortCol: String
          $sortOrder: String
        ) {
          paginatedExtImages(
            pageSize: $pageSize
            page: $page
            search: $search
            filters: $filters
            sortCol: $sortCol
            sortOrder: $sortOrder
          ) {
            extImages {
              id
              status
              extAudit {
                id
                store {
                  id
                  name
                  retailer {
                    id
                    name
                  }
                  banner {
                    id
                    name
                  }
                }
              }
              assignedToUser {
                id
                name
              }
              assignedAt
              completedAt
            }

            pagination {
              page
              pageCount
              pageSize
              total
            }
          }
        }
      `,
      variables,
    }).then(({ data: { paginatedExtImages } }) => {
      commit('setPaginatedExtImages', paginatedExtImages)
      commit('setIsLoadingPaginatedExtImages', false)
    })
  },
  loadExtImages({ commit }) {
    return apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: gql`
        query {
          extImages {
            id
            status
            extAudit {
              id
              store {
                id
                name
                banner {
                  id
                  name
                }
                retailer {
                  id
                  name
                }
              }
            }
            assignedToUser {
              id
              name
            }
            assignedAt
            completedAt
          }
        },
      `,
    }).then(({ data: { extImages } }) => {
      commit('setExtImages', extImages)
    })
  },
  loadExtImageAnnotations({ commit }, variables) {
    return apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: gql`
        query extImageAnnotations($id: Int!) {
          extImageAnnotations(id: $id) {
            boxes {
              left
              top
              right
              bottom
            }
            imgWidth
            imgHeight
            imgTs
          }
        },
      `,
      variables,
    }).then(({ data: { extImageAnnotations } }) => {
      commit('setExtImageAnnotations', extImageAnnotations)
    })
  },
  loadExtImage({ commit }, variables) {
    return apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: gql`
        query extImage($id: Int!) {
          extImage(id: $id) {
            id
            url
            status
            reviewComment
            extAudit {
              id
              store {
                id
                name
                banner {
                  id
                  name
                }
                retailer {
                  id
                  name
                }
              }
            }
            extSections {
              id
              startX
              startY
              width
              height
              status
              extPlacements {
                id
                extMastheadOther
                extPocketOther
                shelf
                position
                isStickerReviewable
                isMastheadReviewable
                isMagazinePresent
                wasMagAuto
                wasStkAuto
                extMasthead {
                  id
                }
                extPocket {
                  id
                }
              }
            }
          }
        },
      `,
      variables,
    }).then(({ data: { extImage } }) => {
      commit('setExtImage', extImage)
    })
  },
  loadNextExtImage({ commit }) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation {
          nextExtImage {
            id
            url
            status
            reviewComment
            extAudit {
              id
              store {
                id
                name
                banner {
                  id
                  name
                }
                retailer {
                  id
                  name
                }
              }
            }
            extSections {
              id
              startX
              startY
              width
              height
              status
              extPlacements {
                id
                extMastheadOther
                extPocketOther
                shelf
                position
                isStickerReviewable
                isMastheadReviewable
                isMagazinePresent
                wasMagAuto
                wasStkAuto
                extMasthead {
                  id
                }
                extPocket {
                  id
                }
              }
            }
          }
        }
      `,
    }).then(({ data: { nextExtImage } }) => {
      // TODO: What if null?
      commit('setExtImage', nextExtImage)
    })
  },
  loadExtMastheads({ commit }, options) {
    return apollo.defaultClient.query({
      /**
       * Optionally bypass the cache.
       */
      fetchPolicy: (options || {}).fetchPolicy,
      query: gql`
        query {
          extMastheads {
            id
            name
          }
        }
      `,
    }).then(({ data: { extMastheads } }) => commit('setExtMastheads', extMastheads))
  },
  loadExtPockets({ commit }, options) {
    return apollo.defaultClient.query({
      /**
       * Optionally bypass the cache.
       */
      fetchPolicy: (options || {}).fetchPolicy,
      query: gql`
        query {
          extPockets {
            id
            name
          }
        }
      `,
    }).then(({ data: { extPockets } }) => commit('setExtPockets', extPockets))
  },
  loadPossibleNextExtImageUrls({ commit }) {
    return apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: gql`
        query loadPossibleNextExtImageUrls {
          loadPossibleNextExtImageUrls {
            id
            url
          }
        }
      `,
    }).then(({ data: { loadPossibleNextExtImageUrls } }) => {
      commit('setNextPossibleUrls', loadPossibleNextExtImageUrls)
    })
  },
  loadRandomExtImage({ commit }, { id }) {
    return apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: gql`
        query randomExtImage($id: Int) {
          randomExtImage(id: $id) {
            id
            url
            extSections {
              startX
              startY
              width
              height
              extPlacements {
                shelf
                position
              }
            }
          }
        },
      `,
      variables: { id },
    }).then(({ data: { randomExtImage } }) => {
      commit('setRandomExtImage', randomExtImage)
      return randomExtImage
    })
  },
  predictBoxesRealtime(context, variables) {
    return apollo.defaultClient.query({
      fetchPolicy: 'network-only',
      query: gql`
        query extImagePredictBoxesRealtime($id: Int!) {
          extImagePredictBoxesRealtime(id: $id)
        },
      `,
      variables,
    }).then(({ data: { extImagePredictBoxesRealtime } }) => extImagePredictBoxesRealtime)
  },
  saveExtImageAnnotations({ state }, annotations) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation updateExtImageAnnotations($id: Int!, $extImageAnnotations: ExtImageAnnotationsInput!) {
          updateExtImageAnnotations(id: $id, extImageAnnotations: $extImageAnnotations)
        }
      `,
      variables: {
        id: state.extImage.id,
        extImageAnnotations: annotations,
      },
    })
  },
  saveExtSections({ commit, state }, sections) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation updateExtImageSections($id: Int!, $extSections: [ExtSectionInput]!) {
          updateExtImageExtSections(id: $id, extSections: $extSections) {
            id
            url
            status
            reviewComment
            extAudit {
              id
              store {
                id
                name
                banner {
                  id
                  name
                }
                retailer {
                  id
                  name
                }
              }
            }
            extSections {
              id
              startX
              startY
              height
              width
              status
              extPlacements {
                id
                extMastheadOther
                extPocketOther
                shelf
                position
                isStickerReviewable
                isMastheadReviewable
                isMagazinePresent
                wasMagAuto
                wasStkAuto
                extMasthead {
                  id
                }
                extPocket {
                  id
                }
              }
            }
          }
        }
      `,
      variables: {
        id: state.extImage.id,
        extSections: sections,
      },
    }).then(({ data: { updateExtImageExtSections } }) => {
      // Returns the updated image object
      commit('setExtImage', updateExtImageExtSections)
    })
  },
  updateExtSectionPlacements(context, variables) {
    // eslint-disable-next-line arrow-body-style
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation updateExtSectionPlacements($id: Int!, $extPlacements: [ExtSectionPlacementInput]!) {
          updateExtSectionPlacements(id: $id, extPlacements: $extPlacements) {
            id
            shelf
            position
          }
        }
      `,
      variables: {
        id: variables.id,
        extPlacements: variables.extPlacements,
      },
    })
  },
  recordExtImageSectionsCompleted({ state }) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation recordExtImageSectionsCompleted($id: Int!) {
          recordExtImageSectionsCompleted(id: $id)
        }
      `,
      variables: {
        id: state.extImage.id,
      },
    })
  },
  recordExtSectionPlacementsStarted(context, variables) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation recordExtSectionPlacementsStarted($id: Int!) {
          recordExtSectionPlacementsStarted(id: $id)
        }
      `,
      variables: {
        id: variables.id,
      },
    })
  },
  recordExtSectionPlacementsFinished(context, variables) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation recordExtSectionPlacementsFinished($id: Int!, $wasPlacementsGuessCorrect: Boolean) {
          recordExtSectionPlacementsFinished(id: $id, wasPlacementsGuessCorrect: $wasPlacementsGuessCorrect)
        }
      `,
      variables,
    })
  },
  rotateImage({ state }, variables = {}) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation rotateExtImage($id: Int!, $rotation: Int) {
          rotateExtImage(id: $id, rotation: $rotation)
        }
      `,
      variables: {
        id: variables.id || state.extImage.id,
        rotation: variables.degrees || state.rotationDegrees,
      },
    })
  },
  markImageUnreviewable({ state }, id = null) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation markExtImageUnreviewable($id: Int!) {
          markExtImageUnreviewable(id: $id)
        }
      `,
      variables: {
        id: id || state.extImage.id,
      },
    })
  },
  markImageComplete({ state }) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation markExtImageComplete($id: Int!) {
          markExtImageComplete(id: $id)
        }
      `,
      variables: {
        id: state.extImage.id,
      },
    })
  },
  flagImageForReview({ state }, fields) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation flagExtImageForReview(
          $id: Int!
          $comment: String!
          $fromSection: Boolean!
        ) {
          flagExtImageForReview(
            id: $id
            comment: $comment
            fromSection: $fromSection
          )
        }
      `,
      variables: {
        id: state.extImage.id,
        comment: fields.comment,
        fromSection: fields.fromSection,
      },
    })
  },
  markImageAsReviewed({ state }) {
    return apollo.defaultClient.mutate({
      mutation: gql`
        mutation markExtImageAsReviewed($id: Int!) {
          markExtImageAsReviewed(id: $id)
        }
      `,
      variables: {
        id: state.extImage.id,
      },
    })
  },
}


export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/ext_audit/ExtAudit.js